import type { Range } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Extension } from '@tiptap/react';

export interface EmbedImagesOptions {
  // MIME types embedded; any other pasted or dropped file is left to the default handling.
  allowedTypes: readonly string[];
}

export const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// "Copy image" in a browser puts the image file beside HTML that is only an <img> of the original URL.
// That HTML adds nothing, and its remote source would not survive saving.
const isOnlyTheImage = (html: string): boolean => {
  const { body } = new DOMParser().parseFromString(html, 'text/html');
  return body.querySelectorAll('img').length === 1 && !body.textContent?.trim();
};

const decodeUri = (text: string): string => {
  try {
    return decodeURI(text);
  } catch {
    return text;
  }
};

// Copying files in Explorer, Finder or a file manager puts their paths, names or file:// URIs in the
// plain text beside the files themselves, one per line.
const isOnlyTheFileNames = (plainText: string, files: FileList): boolean => {
  const names = Array.from(files, (file) => file.name);
  const lines = plainText
    .split(/[\r\n]+/)
    .map((line) => decodeUri(line.trim()))
    .filter(Boolean);
  return lines.every((line) =>
    names.some((name) => line === name || line.endsWith(`/${name}`) || line.endsWith(`\\${name}`))
  );
};

// Word and browsers put an image rendering of copied content beside its markup; paste the markup.
const hasMarkupBesideTheImages = (data: DataTransfer, files: File[]): boolean => {
  const html = data.getData('text/html');
  if (html) {
    return !(files.length === 1 && isOnlyTheImage(html));
  }
  const plainText = data.getData('text/plain');
  return !!plainText && !isOnlyTheFileNames(plainText, data.files);
};

/**
 * Inserts pasted or dropped image files as base64 `data:` images, for editors whose content keeps
 * its images inline instead of uploading them (see `embedImages` in DisplayConfigProps).
 */
const EmbedImages = Extension.create<EmbedImagesOptions>({
  name: 'embedImages',

  addOptions() {
    return { allowedTypes: [] };
  },

  addProseMirrorPlugins() {
    const { editor, options } = this;

    const embeddableImages = (files: FileList | null | undefined): File[] =>
      Array.from(files ?? []).filter((file) => options.allowedTypes.includes(file.type));

    // Read one file at a time so the images land in the order they were given, each after the last.
    const insertImages = async (files: File[], at: number | Range) => {
      let position = at;
      for (const file of files) {
        let src: string;
        try {
          src = await readFileAsDataUrl(file);
        } catch {
          // An unreadable file is skipped; the rest are still inserted.
          continue;
        }
        editor.chain().focus().insertContentAt(position, { type: 'image', attrs: { src } }).run();
        position = editor.state.selection.to;
      }
    };

    return [
      new Plugin({
        key: new PluginKey('embedImages'),
        props: {
          handlePaste: (view, event) => {
            const data = event.clipboardData;
            const files = embeddableImages(data?.files);
            if (!data || !files.length || hasMarkupBesideTheImages(data, files)) {
              return false;
            }
            const { from, to } = view.state.selection;
            insertImages(files, { from, to });
            return true;
          },
          handleDrop: (view, event) => {
            const files = embeddableImages(event.dataTransfer?.files);
            if (!files.length) {
              return false;
            }
            event.preventDefault();
            const dropPosition = view.posAtCoords({ left: event.clientX, top: event.clientY });
            insertImages(files, dropPosition?.pos ?? view.state.selection.from);
            return true;
          },
        },
      }),
    ];
  },
});

export default EmbedImages;
